import { LucideIcon, Sparkles, Target, TrendingUp, Star, CheckCircle, Users, Share2, ClipboardCheck, Zap, Coins, Gift, ShoppingCart, Trophy, Medal, Calendar, Layers, CircleDot, Flame, Gem, Building2, LayoutDashboard, UserPlus, Shield, Handshake, Percent, Network, CreditCard, Settings, Wallet, QrCode, ScanLine, BarChart3, Gauge, ShieldCheck } from "lucide-react";
import { PADEL_LEVELS } from "@/constants/levels";

// Mapping niveau -> icône Lucide
const levelIcons: Record<number, LucideIcon> = {
  1: Target,
  2: TrendingUp,
  3: CircleDot,
  4: Zap,
  5: Flame,
  6: Star,
  7: Gem,
  8: Trophy,
};

// Mapping niveau -> couleur
const levelColors: Record<number, string> = {
  1: 'text-gray-400',
  2: 'text-blue-300',
  3: 'text-blue-500',
  4: 'text-green-400',
  5: 'text-orange-400',
  6: 'text-yellow-400',
  7: 'text-purple-500',
  8: 'text-yellow-500',
};

export interface TutorialSlide {
  title: string;
  content: string;
  icon: LucideIcon;
  iconColor: string;
}

export interface TutorialDefinition {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  category: 'player' | 'match' | 'credits' | 'tournaments' | 'association';
  slides: TutorialSlide[];
}

export const tutorials: TutorialDefinition[] = [
  {
    id: 'rating_system',
    title: 'Niveau, ELO et confiance',
    description: 'Comprenez votre niveau, votre ELO et sa confiance',
    icon: TrendingUp,
    category: 'player',
    // Réécrit le 21/10/2026 avec les stats joueur. L'ancienne version présentait
    // le niveau et le « Rating Elo » comme deux classements indépendants (l'ELO
    // n'est que le niveau traduit en points) et des « étoiles de confiance »
    // comptées en matchs joués, que les absences et forfaits faisaient baisser.
    // Désormais la confiance du niveau est un pourcentage (matchs, régularité,
    // variété, matchs vérifiés), et la fiabilité un signal à part qui ne
    // s'affiche qu'en cas d'incident et ne touche plus le niveau.
    // Les `iconColor` passent sur des jetons, comme association_overview.
    slides: [
      {
        title: 'Bienvenue sur Pala Puntos !',
        content: 'Trois repères décrivent votre jeu : votre niveau, votre ELO et la confiance de ce niveau.',
        icon: Sparkles,
        iconColor: 'text-accent',
      },
      {
        title: 'Votre Niveau (1 à 8)',
        content: 'Vous le déclarez une seule fois à l\'inscription, puis l\'application le fait évoluer après chaque match Challenge, de championnat ou de tournoi. Battre une paire plus forte rapporte davantage.',
        icon: Target,
        iconColor: 'text-primary',
      },
      {
        title: 'Votre ELO',
        content: 'C\'est votre niveau traduit en points, de 800 à 3 000 : il monte et descend avec lui. Un match Amical ne change ni l\'un ni l\'autre.',
        icon: TrendingUp,
        iconColor: 'text-success',
      },
      {
        title: 'La Confiance du niveau',
        content: 'Un pourcentage qui dit si votre niveau est fiable : il grandit avec vos matchs, leur régularité et la variété de vos adversaires. Sous 40 % le niveau est provisoire, à partir de 60 % il est établi.',
        icon: Gauge,
        iconColor: 'text-accent',
      },
      {
        title: 'Votre Fiabilité',
        content: 'Seuls comptent les incidents des 6 derniers mois : absence, forfait tardif, WO non justifié en tournoi. Sans incident rien ne s\'affiche, sinon un badge prévient les autres joueurs, sans toucher votre niveau.',
        icon: ShieldCheck,
        iconColor: 'text-success',
      },
      {
        title: "C'est parti !",
        content: 'Chaque chiffre de l\'accueil ouvre sa courbe dans Profil › Mes stats. Vous y trouverez aussi le détail de votre confiance.',
        icon: CheckCircle,
        iconColor: 'text-accent',
      },
    ],
  },
  {
    id: 'match_creation',
    title: 'Créer un Match',
    description: 'Amical, Challenge et confirmation',
    icon: Users,
    category: 'match',
    slides: [
      {
        title: 'Amical vs Challenge',
        content: 'Match Amical = ne change ni votre niveau ni votre ELO, parfait pour s\'entraîner. Match Challenge = fait évoluer votre niveau et votre ELO, pour les compétiteurs !',
        icon: Users,
        iconColor: 'text-primary',
      },
      {
        title: 'Inviter des Joueurs',
        content: 'Partagez le lien unique de votre match ou invitez directement vos amis depuis l\'application.',
        icon: Share2,
        iconColor: 'text-purple-500',
      },
      {
        title: 'Confirmer les Scores',
        content: 'Après le match, les deux équipes doivent confirmer le résultat. Cela garantit l\'intégrité des scores.',
        icon: ClipboardCheck,
        iconColor: 'text-green-500',
      },
      {
        title: 'Impact sur le Niveau',
        content: 'Seuls les matchs Challenge avec 4 joueurs inscrits et scores confirmés font évoluer votre niveau et votre ELO.',
        icon: Zap,
        iconColor: 'text-accent',
      },
    ],
  },
  {
    id: 'credits_system',
    title: 'Les Crédits',
    description: 'Réserver terrains et leçons',
    icon: Coins,
    category: 'credits',
    slides: [
      {
        title: 'Les Crédits Pala',
        content: 'Les crédits sont une monnaie virtuelle pour réserver des terrains et des leçons dans les clubs partenaires.',
        icon: Coins,
        iconColor: 'text-accent',
      },
      {
        title: 'Obtenir des Crédits',
        content: 'Achetez des packs de crédits directement dans l\'app, ou recevez-en de votre club ou association.',
        icon: Gift,
        iconColor: 'text-green-500',
      },
      {
        title: 'Utilisation',
        content: 'Le coût en crédits varie selon le club et l\'horaire. En général, 1 crédit ≈ 30 minutes de jeu.',
        icon: ShoppingCart,
        iconColor: 'text-blue-500',
      },
    ],
  },
  {
    id: 'tournaments_intro',
    title: 'Les Tournois',
    description: 'Inscription et participation',
    icon: Trophy,
    category: 'tournaments',
    slides: [
      {
        title: 'Types de Tournois',
        content: 'FFT Officiel pour les licenciés, Loisir pour tous, ou tournois internes de votre club. Choisissez selon votre niveau !',
        icon: Trophy,
        iconColor: 'text-accent',
      },
      {
        title: 'Inscription',
        content: 'Créez votre équipe (en double) et inscrivez-vous. Certains tournois sont payants, d\'autres gratuits.',
        icon: Medal,
        iconColor: 'text-purple-500',
      },
      {
        title: 'Déroulement',
        content: 'Consultez le tableau et vos matchs programmés. Recevez des notifications pour ne rien manquer !',
        icon: Calendar,
        iconColor: 'text-blue-500',
      },
    ],
  },
  {
    id: 'padel_levels',
    title: 'Les Niveaux de Padel',
    description: 'Comprendre les 8 niveaux de jeu',
    icon: Layers,
    category: 'player',
    slides: [
      {
        title: 'Les 8 Niveaux de Padel',
        content: 'De débutant à professionnel, découvrez où vous vous situez sur l\'échelle officielle FFT 2025.',
        icon: Layers,
        iconColor: 'text-accent',
      },
      ...PADEL_LEVELS.map(level => ({
        title: `Niveau ${level.level} - ${level.title.split(' / ')[0]}`,
        content: level.description,
        icon: levelIcons[level.level] || Target,
        iconColor: levelColors[level.level] || 'text-gray-400',
      })),
      {
        title: 'Votre niveau évoluera !',
        content: 'Jouez régulièrement et votre niveau sera automatiquement ajusté selon vos performances. Bonne progression !',
        icon: TrendingUp,
        iconColor: 'text-green-500',
      },
    ],
  },
  // =============================================
  // Tutoriels Association
  // =============================================
  {
    id: 'association_overview',
    title: 'Bienvenue sur votre Association',
    description: 'Découvrez votre espace de gestion',
    icon: Building2,
    category: 'association',
    // Réécrit le 26/09/2026 avec la refonte du profil. L'ancienne version
    // décrivait une interface qui n'existe plus — « tuiles d'actions rapides »
    // (supprimées de l'accueil : elles recopiaient la navigation), « menu
    // latéral » (devenu un tiroir sur téléphone) et des onglets « Clubs » et
    // « Crédits » fusionnés dans « Avantages clubs ». Ce tutoriel s'ouvre TOUT
    // SEUL au premier accès : il était la première chose que voyait un nouveau
    // bureau, et il lui décrivait une autre application.
    // Les `iconColor` passent sur des jetons : `text-blue-500`,
    // `text-purple-500` et `text-green-500` ne basculaient pas en sombre.
    slides: [
      {
        title: 'Votre espace association',
        content: 'Bienvenue ! Tout ce que gère votre bureau est ici : adhérents, cotisations, événements, compétitions et avantages négociés auprès des clubs partenaires.',
        icon: Building2,
        iconColor: 'text-accent',
      },
      {
        title: 'L\'accueil dit ce qui vous attend',
        content: 'En haut, les demandes qui attendent une décision du bureau. En dessous, vos quatre chiffres clés, l\'agenda des prochains jours et l\'activité récente de l\'association.',
        icon: LayoutDashboard,
        iconColor: 'text-accent',
      },
      {
        title: 'Naviguer : la balle et le menu',
        content: 'La balle jaune, au centre de la barre du bas, ouvre les gestes du quotidien — inviter un membre, valider un bon, créer un événement, écrire aux adhérents. Le logo, en haut à gauche, ouvre le menu complet rangé en trois groupes : Association, Animation, Gestion.',
        icon: Layers,
        iconColor: 'text-accent',
      },
      {
        title: 'Premiers pas',
        content: 'Dans l\'ordre : 1) complétez la fiche publique dans Paramètres, 2) invitez vos adhérents depuis Membres, 3) affiliez un club dans Avantages clubs pour leur négocier des crédits.',
        icon: CheckCircle,
        iconColor: 'text-accent',
      },
    ],
  },
  {
    id: 'association_members',
    title: 'Gestion des Membres',
    description: 'Inviter, approuver et gérer vos membres',
    icon: Users,
    category: 'association',
    slides: [
      {
        title: 'Inviter des membres',
        content: 'Invitez vos membres par email ou générez un lien d\'invitation partageable. Ils recevront une notification pour rejoindre votre association.',
        icon: UserPlus,
        iconColor: 'text-blue-500',
      },
      {
        title: 'Quotas et limites',
        content: 'Le nombre de membres dépend de votre abonnement. Consultez votre quota restant dans l\'onglet Abonnement.',
        icon: Users,
        iconColor: 'text-amber-500',
      },
      {
        title: 'Flux d\'approbation',
        content: 'Les nouveaux membres apparaissent dans l\'onglet Validations. Approuvez ou refusez chaque demande d\'adhésion.',
        icon: ClipboardCheck,
        iconColor: 'text-green-500',
      },
      {
        title: 'Rôles et statuts',
        content: 'Suivez le statut de chaque membre : actif, en attente ou suspendu. Envoyez des messages directs depuis la liste des membres.',
        icon: Shield,
        iconColor: 'text-purple-500',
      },
    ],
  },
  {
    id: 'association_partner_clubs',
    title: 'Clubs Affiliés',
    description: 'Construisez votre réseau de clubs partenaires',
    icon: Building2,
    category: 'association',
    slides: [
      {
        title: 'Ajouter un club',
        content: 'Deux options : sélectionnez un club déjà inscrit sur Pala Puntos, ou ajoutez manuellement un club externe avec ses coordonnées.',
        icon: Building2,
        iconColor: 'text-blue-500',
      },
      {
        title: 'Types de partenariat',
        content: 'Affilié, Partenaire ou Sponsor : chaque niveau de partenariat détermine les avantages et accords tarifaires possibles.',
        icon: Handshake,
        iconColor: 'text-purple-500',
      },
      {
        title: 'Accords de remise',
        content: 'Négociez des tarifs préférentiels sur les réservations de terrains pour vos membres auprès de chaque club partenaire.',
        icon: Percent,
        iconColor: 'text-green-500',
      },
      {
        title: 'Suivi du réseau',
        content: 'Visualisez vos partenariats actifs, en attente ou suspendus. Gérez le cycle de vie de chaque relation club.',
        icon: Network,
        iconColor: 'text-accent',
      },
    ],
  },
  {
    id: 'association_credits',
    title: 'Subventions & Crédits',
    description: 'Configurez et distribuez des subventions',
    icon: CreditCard,
    category: 'association',
    slides: [
      {
        title: 'Le système de subventions',
        content: 'Votre association peut subventionner les activités de ses membres dans les clubs partenaires grâce au système de crédits.',
        icon: CreditCard,
        iconColor: 'text-accent',
      },
      {
        title: 'Règles de subvention',
        content: 'Créez des règles de subvention : montant fixe ou pourcentage, par club ou global, avec un budget plafonné et des dates de validité.',
        icon: Settings,
        iconColor: 'text-blue-500',
      },
      {
        title: 'Demandes de crédits',
        content: 'Les membres soumettent des demandes de crédits. Vous les retrouvez dans l\'onglet Validations pour les approuver ou les refuser.',
        icon: ClipboardCheck,
        iconColor: 'text-amber-500',
      },
      {
        title: 'Allocations de crédits',
        content: 'Distribuez proactivement des crédits à vos membres, individuellement ou par groupe, sans attendre leurs demandes.',
        icon: Gift,
        iconColor: 'text-green-500',
      },
      {
        title: 'Suivi budgétaire',
        content: 'Suivez vos dépenses dans le Portefeuille : historique des transactions, solde en temps réel et export CSV.',
        icon: Wallet,
        iconColor: 'text-purple-500',
      },
    ],
  },
  {
    id: 'association_vouchers',
    title: 'Stock de Crédits & Vouchers',
    description: 'Achetez du stock et générez des bons QR',
    icon: QrCode,
    category: 'association',
    slides: [
      {
        title: 'Acheter du stock',
        content: 'Achetez des packs de crédits auprès de vos clubs partenaires à tarif négocié. Le stock apparaît dans l\'onglet Portefeuille > Stock.',
        icon: ShoppingCart,
        iconColor: 'text-blue-500',
      },
      {
        title: 'Enregistrer un achat',
        content: 'Enregistrez chaque achat de crédits avec le mode de paiement (virement, chèque, espèces). Le club confirme la réception.',
        icon: ClipboardCheck,
        iconColor: 'text-green-500',
      },
      {
        title: 'Générer des vouchers',
        content: 'Quand vous approuvez une demande de crédits, un bon (voucher) avec un code unique et un QR code est généré pour le membre.',
        icon: QrCode,
        iconColor: 'text-purple-500',
      },
      {
        title: 'Validation sur site',
        content: 'Le membre présente son QR code au club. Le staff scanne ou saisit le code pour valider le bon et créditer le compte du joueur.',
        icon: ScanLine,
        iconColor: 'text-accent',
      },
    ],
  },
  {
    id: 'association_bureau',
    title: 'Bureau & Permissions',
    description: 'Gérez votre équipe dirigeante',
    icon: Shield,
    category: 'association',
    slides: [
      {
        title: 'L\'équipe dirigeante',
        content: 'Constituez votre bureau : Président, Vice-Président, Secrétaire, Trésorier et membres du bureau. Chacun a un rôle défini.',
        icon: Users,
        iconColor: 'text-blue-500',
      },
      {
        title: 'Permissions par rôle',
        content: 'Attribuez des permissions granulaires à chaque membre du bureau : événements, tournois, membres, messagerie, finances, paramètres.',
        icon: Shield,
        iconColor: 'text-purple-500',
      },
      {
        title: 'Gestion collaborative',
        content: 'Plusieurs membres du bureau peuvent gérer l\'association simultanément, chacun avec ses propres droits d\'accès.',
        icon: Handshake,
        iconColor: 'text-green-500',
      },
    ],
  },
  {
    id: 'association_membership_fees',
    title: 'Cotisations',
    description: 'Configurez les formules d\'adhésion',
    icon: Coins,
    category: 'association',
    slides: [
      {
        title: 'Formules de cotisation',
        content: 'Créez des formules d\'adhésion adaptées : annuelle, mensuelle, trimestrielle ou paiement unique. Définissez le montant et la description.',
        icon: CreditCard,
        iconColor: 'text-accent',
      },
      {
        title: 'Paiement en ligne',
        content: 'Vos membres peuvent régler leur cotisation directement via Stripe dans l\'application, en toute sécurité.',
        icon: Wallet,
        iconColor: 'text-green-500',
      },
      {
        title: 'Suivi des paiements',
        content: 'Consultez qui a payé, les montants reçus et le statut de chaque adhésion. Gardez le contrôle sur vos finances.',
        icon: BarChart3,
        iconColor: 'text-blue-500',
      },
    ],
  },
];

export const getTutorialById = (id: string): TutorialDefinition | undefined => {
  return tutorials.find(t => t.id === id);
};

export const getTutorialsByCategory = (category: TutorialDefinition['category']): TutorialDefinition[] => {
  return tutorials.filter(t => t.category === category);
};
